import {
	addOrganizationFormUniqueField,
	getOrganizationFormUniqueFields,
	updateOrganizationFormUniqueFields,
} from "@/services/organization-service";
import { PlusOutlined } from "@ant-design/icons";
import {
	Button,
	Form,
	Input,
	Modal,
	Popconfirm,
	Select,
	Space,
	Switch,
	Table,
	message,
} from "antd";
import type { FC } from "react";
import { useEffect, useState } from "react";

export interface CustomField {
	id: string;
	name: string;
	label: string;
	type: "text" | "number" | "date" | "select" | "textarea";
	required: boolean;
	options?: string[]; // For select type
	placeholder?: string;
	conditional?: {
		dependsOn: string;
		operator?:
			| "equals"
			| "notEquals"
			| "in"
			| "notIn"
			| "greaterThan"
			| "lessThan"
			| "contains";
		value?: unknown;
		values?: unknown[];
	};
}

interface CustomFieldsModalProps {
	visible: boolean;
	onClose: () => void;
	onSuccess: () => void;
	currentFields: CustomField[] | null;
	organizationId: number;
	formId: number; // Add formId prop
}

export const CustomFieldsModal: FC<CustomFieldsModalProps> = ({
	visible,
	onClose,
	onSuccess,
	currentFields,
	organizationId,
	formId,
}) => {
	const [form] = Form.useForm();
	const [loading, setLoading] = useState(false);
	const [fetching, setFetching] = useState(false);
	const [fields, setFields] = useState<CustomField[]>(currentFields || []);
	const [editingField, setEditingField] = useState<CustomField | null>(null);

	// Fetch fields from API when modal opens
	useEffect(() => {
		if (visible && organizationId && formId) {
			const fetchFields = async () => {
				try {
					setFetching(true);
					const fetchedFields = await getOrganizationFormUniqueFields(formId);
					setFields(fetchedFields || []);
				} catch (error: any) {
					console.error("Failed to fetch custom fields:", error);
					// Fallback to currentFields if API call fails
					setFields(currentFields || []);
				} finally {
					setFetching(false);
				}
			};
			fetchFields();
		} else if (visible) {
			// Fallback to currentFields if no orgId/formId
			setFields(currentFields || []);
		}
		setEditingField(null);
		form.resetFields();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [visible, organizationId, formId]);

	const handleAddField = () => {
		const newField: CustomField = {
			id: `field-${Date.now()}`,
			name: "",
			label: "",
			type: "text",
			required: false,
		};
		setEditingField(newField);
		form.setFieldsValue(newField);
	};

	const handleEditField = (field: CustomField) => {
		setEditingField(field);
		form.setFieldsValue(field);
	};

	const handleDeleteField = (fieldId: string) => {
		setFields(fields.filter((f) => f.id !== fieldId));
		if (editingField?.id === fieldId) {
			setEditingField(null);
			form.resetFields();
		}
	};

	const handleSaveField = async (values: CustomField) => {
		if (!values.name || !values.label) {
			message.error("Field name and label are required");
			return;
		}

		// Validate field name format (should be valid for JSON keys)
		if (!/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(values.name)) {
			message.error(
				"Field name must start with a letter or underscore and contain only letters, numbers, and underscores",
			);
			return;
		}

		// Check for duplicate names
		const existingField = fields.find(
			(f) => f.name === values.name && f.id !== editingField?.id,
		);
		if (existingField) {
			message.error("A field with this name already exists");
			return;
		}

		try {
			if (editingField && fields.find((f) => f.id === editingField.id)) {
				// Update existing field - just update local state, will save all on "Save Changes"
				setFields(
					fields.map((f) =>
						f.id === editingField.id ? { ...values, id: editingField.id } : f,
					),
				);
				setEditingField(null);
				form.resetFields();
			} else {
				// Add new field - save immediately to API
				setLoading(true);

				// Clean up the field: remove conditional if it's empty or has no dependsOn
				const cleanedField: CustomField = {
					...values,
					id: `field-${Date.now()}`,
				};
				if (cleanedField.conditional) {
					if (
						!cleanedField.conditional.dependsOn ||
						(!cleanedField.conditional.value &&
							!cleanedField.conditional.values &&
							!cleanedField.conditional.operator)
					) {
						// Remove conditional if it's empty or incomplete
						delete cleanedField.conditional;
					}
				}

				// Optimistically update local state first
				setFields([...fields, cleanedField]);
				setEditingField(null);
				form.resetFields();

				try {
					await addOrganizationFormUniqueField(formId, cleanedField);

					// Refresh from API to ensure sync
					const updatedFields = await getOrganizationFormUniqueFields(formId);
					if (updatedFields && Array.isArray(updatedFields)) {
						setFields(updatedFields);
					}
					// If refresh fails, keep the optimistic update

					message.success("Field added successfully");
					// Notify parent to refresh the field count
					onSuccess();
				} catch (error: any) {
					// If save fails, remove the optimistic update
					setFields(fields.filter((f) => f.id !== cleanedField.id));
					throw error;
				}
			}
		} catch (error: any) {
			message.error(error.message || "Failed to save field");
		} finally {
			setLoading(false);
		}
	};

	const handleCancelEdit = () => {
		setEditingField(null);
		form.resetFields();
	};

	const handleSave = async () => {
		try {
			setLoading(true);

			// Debug: Log current fields state
			console.log("Saving fields:", fields);
			console.log("Fields length:", fields.length);

			// Clean up fields: remove empty conditional objects
			const cleanedFields = fields.map((field) => {
				const cleaned = { ...field };
				if (cleaned.conditional) {
					if (
						!cleaned.conditional.dependsOn ||
						(!cleaned.conditional.value &&
							!cleaned.conditional.values &&
							!cleaned.conditional.operator)
					) {
						// Remove conditional if it's empty or incomplete
						delete cleaned.conditional;
					}
				}
				return cleaned;
			});

			// Always use the current fields state (which includes any local edits)
			// This ensures we save the state the user sees in the UI
			// For form_id 3, send empty array instead of null if no fields (backend will handle conversion)
			const uniqueFieldsValue = cleanedFields.length === 0 ? [] : cleanedFields;

			console.log("Sending to API:", uniqueFieldsValue);

			await updateOrganizationFormUniqueFields(formId, uniqueFieldsValue);

			message.success("Custom fields saved successfully");
			onSuccess();
			onClose();
		} catch (error: any) {
			console.error("Error saving fields:", error);
			message.error(error.message || "Failed to save custom fields");
		} finally {
			setLoading(false);
		}
	};

	const handleCancel = () => {
		setFields(currentFields || []);
		setEditingField(null);
		form.resetFields();
		onClose();
	};

	const fieldColumns = [
		{
			title: "Name",
			dataIndex: "name",
			key: "name",
		},
		{
			title: "Label",
			dataIndex: "label",
			key: "label",
		},
		{
			title: "Type",
			dataIndex: "type",
			key: "type",
			render: (type: string) => type.charAt(0).toUpperCase() + type.slice(1),
		},
		{
			title: "Required",
			dataIndex: "required",
			key: "required",
			render: (required: boolean) => (required ? "Yes" : "No"),
		},
		{
			title: "Actions",
			key: "actions",
			render: (_: any, record: CustomField) => (
				<Space>
					<Button
						type="link"
						size="small"
						onClick={() => handleEditField(record)}
					>
						Edit
					</Button>
					<Popconfirm
						title="Are you sure you want to delete this field?"
						onConfirm={() => handleDeleteField(record.id)}
						okText="Yes"
						cancelText="No"
					>
						<Button type="link" danger size="small">
							Delete
						</Button>
					</Popconfirm>
				</Space>
			),
		},
	];

	return (
		<Modal
			title="Manage Custom Form Fields"
			open={visible}
			onCancel={handleCancel}
			footer={[
				<Button key="cancel" onClick={handleCancel}>
					Cancel
				</Button>,
				<Button
					key="save"
					type="primary"
					onClick={handleSave}
					loading={loading}
				>
					Save Changes
				</Button>,
			]}
			width={1000}
		>
			{fetching ? (
				<div className="text-center py-8 text-gray-500">
					Loading custom fields...
				</div>
			) : (
				<div className="space-y-6">
					{/* Current Fields Table */}
					<div>
						<div className="flex justify-between items-center mb-4">
							<h3 className="text-lg font-medium">Custom Fields</h3>
							<Button
								type="primary"
								icon={<PlusOutlined />}
								onClick={handleAddField}
								disabled={!!editingField}
							>
								Add Field
							</Button>
						</div>
						{fields.length === 0 ? (
							<div className="text-center py-8 text-gray-500">
								No custom fields defined. Click "Add Field" to create one.
							</div>
						) : (
							<Table
								columns={fieldColumns}
								dataSource={fields}
								rowKey="id"
								pagination={false}
								size="small"
							/>
						)}
					</div>

					{/* Add/Edit Field Form */}
					{editingField && (
						<div className="border-t pt-6">
							<h3 className="text-lg font-medium mb-4">
								{editingField.id && fields.find((f) => f.id === editingField.id)
									? "Edit Field"
									: "Add New Field"}
							</h3>
							<Form
								form={form}
								layout="vertical"
								onFinish={handleSaveField}
								initialValues={editingField}
							>
								<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
									<Form.Item
										name="name"
										label="Field Name"
										rules={[
											{ required: true, message: "Field name is required" },
											{
												pattern: /^[a-zA-Z_][a-zA-Z0-9_]*$/,
												message:
													"Must start with a letter or underscore and contain only letters, numbers, and underscores",
											},
										]}
										tooltip="Internal field name (used in database). Must be unique and start with a letter or underscore."
									>
										<Input
											placeholder="e.g., custom_field_1"
											disabled={!!fields.find((f) => f.id === editingField.id)}
										/>
									</Form.Item>

									<Form.Item
										name="label"
										label="Field Label"
										rules={[
											{ required: true, message: "Field label is required" },
										]}
										tooltip="Display label shown to users in the form"
									>
										<Input placeholder="e.g., Custom Field 1" />
									</Form.Item>

									<Form.Item
										name="type"
										label="Field Type"
										rules={[
											{ required: true, message: "Field type is required" },
										]}
									>
										<Select
											options={[
												{ value: "text", label: "Text" },
												{ value: "textarea", label: "Text Area" },
												{ value: "number", label: "Number" },
												{ value: "date", label: "Date" },
												{ value: "select", label: "Select (Dropdown)" },
											]}
										/>
									</Form.Item>

									<Form.Item
										name="required"
										label="Required"
										valuePropName="checked"
									>
										<Switch />
									</Form.Item>

									<Form.Item name="placeholder" label="Placeholder Text">
										<Input placeholder="Enter placeholder text" />
									</Form.Item>

									<Form.Item
										noStyle
										shouldUpdate={(prevValues, currentValues) =>
											prevValues.type !== currentValues.type
										}
									>
										{({ getFieldValue }) => {
											const fieldType = getFieldValue("type");
											if (fieldType === "select") {
												return (
													<Form.Item
														name="options"
														label="Options (comma-separated)"
														rules={[
															{
																required: true,
																message:
																	"Options are required for select fields",
															},
														]}
														tooltip="Enter options separated by commas (e.g., Option 1, Option 2, Option 3)"
													>
														<Input
															placeholder="Option 1, Option 2, Option 3"
															onChange={(e) => {
																const value = e.target.value;
																form.setFieldsValue({
																	options: value
																		.split(",")
																		.map((opt) => opt.trim())
																		.filter(Boolean),
																});
															}}
														/>
													</Form.Item>
												);
											}
											return null;
										}}
									</Form.Item>
								</div>

								{/* Conditional Logic Section */}
								<div className="border-t pt-4 mt-4">
									<h4 className="text-md font-medium mb-4">
										Conditional Logic (Optional)
									</h4>
									<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
										<Form.Item
											name={["conditional", "dependsOn"]}
											label="Depends On Field"
											tooltip="The field name this field depends on (e.g., assetLocation)"
										>
											<Input placeholder="e.g., assetLocation" />
										</Form.Item>

										<Form.Item
											name={["conditional", "operator"]}
											label="Operator"
											tooltip="Comparison operator"
										>
											<Select
												placeholder="Select operator"
												options={[
													{ value: "equals", label: "Equals" },
													{ value: "notEquals", label: "Not Equals" },
													{ value: "in", label: "In" },
													{ value: "notIn", label: "Not In" },
													{ value: "greaterThan", label: "Greater Than" },
													{ value: "lessThan", label: "Less Than" },
													{ value: "contains", label: "Contains" },
												]}
											/>
										</Form.Item>

										<Form.Item
											noStyle
											shouldUpdate={(prevValues, currentValues) =>
												prevValues?.conditional?.operator !==
												currentValues?.conditional?.operator
											}
										>
											{({ getFieldValue }) => {
												const operator = getFieldValue([
													"conditional",
													"operator",
												]);
												const isArrayOperator =
													operator === "in" || operator === "notIn";

												if (!operator) return null;

												if (isArrayOperator) {
													return (
														<Form.Item
															name={["conditional", "values"]}
															label="Values (comma-separated)"
															tooltip="Enter values separated by commas"
														>
															<Input
																placeholder="Value 1, Value 2, Value 3"
																onChange={(e) => {
																	const value = e.target.value;
																	const parsedValues = value
																		.split(",")
																		.map((v) => {
																			const trimmed = v.trim();
																			// Try to parse as number if possible
																			const num = Number(trimmed);
																			return isNaN(num) ? trimmed : num;
																		})
																		.filter((v) => v !== "");
																	form.setFieldsValue({
																		conditional: {
																			...form.getFieldValue("conditional"),
																			values: parsedValues,
																		},
																	});
																}}
															/>
														</Form.Item>
													);
												}

												return (
													<Form.Item
														name={["conditional", "value"]}
														label="Value"
														tooltip="Value to compare against"
													>
														<Input
															placeholder="Enter value"
															onChange={(e) => {
																const value = e.target.value;
																// Try to parse as number if possible
																const num = Number(value);
																form.setFieldsValue({
																	conditional: {
																		...form.getFieldValue("conditional"),
																		value: isNaN(num) ? value : num,
																	},
																});
															}}
														/>
													</Form.Item>
												);
											}}
										</Form.Item>
									</div>
								</div>

								<div className="flex justify-end space-x-2 mt-4">
									<Button onClick={handleCancelEdit}>Cancel</Button>
									<Button type="primary" htmlType="submit">
										{editingField.id &&
										fields.find((f) => f.id === editingField.id)
											? "Update Field"
											: "Add Field"}
									</Button>
								</div>
							</Form>
						</div>
					)}
				</div>
			)}
		</Modal>
	);
};
